// Single source of truth for constructing each pane section. Both the boot
// build (`initializePane`) and the per-section rebuild (`UI_PANE_REBUILD_SECTION`)
// drive the same builders through this one registry, so each section's deps and
// the scene-adapter helpers live in exactly one place — no boot/rebuild drift.
//
// Folder refs round-trip through `paneState.refs` so a rebuild finds-and-reuses
// the existing folder; the per-section input refs are nulled so the builder
// re-grabs them after it rebuilds the folder's contents.

import * as THREE from 'three';
import { paneState } from './state';
import { params as paramsProxy } from '@store/instance';
import { openReductionPlot } from '../reductions/plotWindow';
import { buildSlicesSection } from './panes/slices';
import { buildVolumeSection } from './panes/volume';
import { buildContourSection } from './panes/contour';
import { buildFieldLinesSection } from './panes/fieldlines';
import { buildSelectionsSection } from './panes/selections';
import { buildReductionsSection } from './panes/reductions';
import { forEachContourMaterial, getContourRange, setVolumeUniform } from './sectionHelpers';
import type { SectionRebuildKey } from '@store/intents';
import type { ViewerContext } from '@app/context';
import type { Refs, TPaneLike } from './types';

/** Sections with a folder builder (the rail tabs that own a pane folder).
 *  Derived from the intent vocabulary by removing the two non-folder keys, so
 *  `@store/intents` stays the single source of truth for section names. */
export type BuildableSection = Exclude<SectionRebuildKey, 'data' | 'selection-focus'>;

/** The two callbacks the slices builder needs that aren't derivable from the
 *  viewer — supplied by bootstrap (scene slice display + grid rebuild). */
export interface SliceDeps {
    applySliceDisplaySettings: () => void;
    rebuildSlicesForCurrentGrid: () => void;
}

export interface SectionBuildCtx {
    /** Live pane instance — read through a getter so a rebuilt pane is picked up. */
    readonly pane: TPaneLike;
    readonly viewer: ViewerContext;
    /** The reactive params proxy (writes route through `store.set`). Boot used
     *  this; the rebuild path historically bound raw `state.params` — unifying
     *  on the proxy makes rebuilt panes behave identically to boot-built ones. */
    readonly params: Record<string, unknown>;
    readonly applySliceDisplaySettings: () => void;
    readonly rebuildSlicesForCurrentGrid: () => void;
}

export function makeSectionCtx(viewer: ViewerContext, slice: SliceDeps): SectionBuildCtx {
    return {
        get pane(): TPaneLike { return paneState.instance as TPaneLike; },
        viewer,
        get params(): Record<string, unknown> { return paramsProxy as unknown as Record<string, unknown>; },
        applySliceDisplaySettings: () => { try { slice.applySliceDisplaySettings(); } catch { /* swallow */ } },
        rebuildSlicesForCurrentGrid: () => { try { slice.rebuildSlicesForCurrentGrid(); } catch { /* swallow */ } },
    };
}

function refs(): Refs { return paneState.refs; }

const BUILDERS: Record<BuildableSection, (ctx: SectionBuildCtx) => void> = {
    slices(ctx) {
        const r = refs();
        const out = buildSlicesSection(ctx.pane, {
            slicesFolderRef: r['slicesFolderRef'],
            // Null the input cell refs so the builder re-grabs them against the
            // freshly rebuilt rows; the folder ref is reused (find-or-create).
            sliceZInputRef: null, sliceZYInputRef: null, sliceZXInputRef: null,
        }, {
            params: ctx.params,
            grid: ctx.viewer.grid,
            developerMode: ctx.viewer.sceneMeta.developerMode,
            applySliceDisplaySettings: ctx.applySliceDisplaySettings,
            rebuildSlicesForCurrentGrid: () => { ctx.rebuildSlicesForCurrentGrid(); ctx.applySliceDisplaySettings(); },
        });
        r['slicesFolderRef'] = out['slicesFolderRef'] ?? r['slicesFolderRef'];
        r['sliceZInputRef'] = out['sliceZInputRef'];
        r['sliceZYInputRef'] = out['sliceZYInputRef'];
        r['sliceZXInputRef'] = out['sliceZXInputRef'];
        ctx.applySliceDisplaySettings();
    },
    volume(ctx) {
        const r = refs();
        const out = buildVolumeSection(ctx.pane, { volumeFolderRef: r['volumeFolderRef'] }, {
            params: ctx.params,
            getVolumeUniformSetters: () => ({
                setSteps:      (v: number) => setVolumeUniform(ctx.viewer, 'uSteps', v),
                setDensity:    (v: number) => setVolumeUniform(ctx.viewer, 'uDensity', v),
                setBrightness: (v: number) => setVolumeUniform(ctx.viewer, 'uBrightness', v),
            }),
        });
        r['volumeFolderRef'] = out['volumeFolderRef'] ?? r['volumeFolderRef'];
    },
    contour(ctx) {
        const r = refs();
        const v = ctx.viewer;
        const out = buildContourSection(ctx.pane, { contourFolderRef: r['contourFolderRef'] }, {
            params: ctx.params,
            grid: v.grid,
            applyContourMetalness:    (val: number) => forEachContourMaterial(v, (m) => { if (typeof m.metalness === 'number') m.metalness = val; }),
            applyContourRoughness:    (val: number) => forEachContourMaterial(v, (m) => { if (typeof m.roughness === 'number') m.roughness = val; }),
            applyContourEnvIntensity: (val: number) => forEachContourMaterial(v, (m) => { if (typeof m.envMapIntensity === 'number') m.envMapIntensity = val; }),
            applyContourDoubleSide:   (val: boolean) => forEachContourMaterial(v, (m) => { if (typeof m.side !== 'undefined') m.side = val ? THREE.DoubleSide : THREE.FrontSide; }),
            applyContourDepthWrite:   (val: boolean) => forEachContourMaterial(v, (m) => { if (typeof m.depthWrite === 'boolean') m.depthWrite = !!val; }),
            getContourRange: () => getContourRange(v),
            developerMode: v.sceneMeta.developerMode,
        });
        r['contourFolderRef'] = out['contourFolderRef'] ?? r['contourFolderRef'];
    },
    fieldlines(ctx) {
        const r = refs();
        const out = buildFieldLinesSection(ctx.pane, { fieldLinesFolderRef: r['fieldLinesFolderRef'] }, {
            params: ctx.params,
            developerMode: ctx.viewer.sceneMeta.developerMode,
        });
        r['fieldLinesFolderRef'] = out['fieldLinesFolderRef'] ?? r['fieldLinesFolderRef'];
    },
    selection(ctx) {
        const r = refs();
        const out = buildSelectionsSection(ctx.pane, { selectionsFolderRef: r['selectionsFolderRef'] }, {
            params: ctx.params,
            grid: ctx.viewer.grid,
        });
        r['selectionsFolderRef'] = out['selectionsFolderRef'] ?? r['selectionsFolderRef'];
    },
    reductions(ctx) {
        const r = refs();
        const out = buildReductionsSection(ctx.pane, { reductionsFolderRef: r['reductionsFolderRef'] }, {
            fieldMeta: ctx.viewer.field.metadata,
            openPlot: (id) => openReductionPlot(id),
        });
        r['reductionsFolderRef'] = out['reductionsFolderRef'] ?? r['reductionsFolderRef'];
    },
};

// Boot order mirrors the rail workflow: pick a region → render it
// (slices/volume/contour) → derive from it (field lines) → extract (reductions).
const BOOT_ORDER: readonly BuildableSection[] = ['slices', 'fieldlines', 'selection', 'volume', 'contour', 'reductions'];

export function buildSection(ctx: SectionBuildCtx, key: BuildableSection): void {
    BUILDERS[key](ctx);
}

export function buildAllSections(ctx: SectionBuildCtx): void {
    for (const key of BOOT_ORDER) BUILDERS[key](ctx);
}
